/**
 * The size a pin was placed at (TOADO-828), and what it says when that is not
 * the size of the page now.
 *
 * `width` and `height` are the browser window's inner size when the pin was
 * placed. `frame` is the box of the frame the pinned element was in, when it
 * was in one: an app builder's preview stage is a frame inside the page, so a
 * pin placed in a tablet-sized stage in a wide window was placed at tablet
 * width, not at the window's.
 */
export interface PlacedSize {
  width: number;
  height: number;
  frame?: { width: number; height: number };
}

export type SizeBand = 'phone' | 'tablet' | 'desktop';

/**
 * Where the bands change, by width in CSS pixels: under 600 is a phone
 * layout, under 1024 a tablet, and the rest desktop. Common breakpoints sit at
 * or near both (Bootstrap's 576 and 992, Tailwind's 640 and 1024), and a
 * tablet in portrait is 768 to 834 wide.
 */
export const TABLET_FROM = 600;
export const DESKTOP_FROM = 1024;

/** Larger than any screen; anything past it is not a size. */
const MAX_SIDE = 20_000;

export function sizeBand(width: number): SizeBand {
  return width < TABLET_FROM ? 'phone' : width < DESKTOP_FROM ? 'tablet' : 'desktop';
}

/** The width the pin's page had: its frame's, when it was placed in one. */
export function placedWidth(placed: PlacedSize): number {
  return placed.frame?.width ?? placed.width;
}

/**
 * Whether a pin whose element is not found was placed at a clearly other size
 * than the window is now: a different band, not a few pixels. A pin with no
 * recorded size is not.
 */
export function placedAtAnotherSize(placed: PlacedSize | undefined, windowWidth: number): boolean {
  return placed !== undefined && sizeBand(placedWidth(placed)) !== sizeBand(windowWidth);
}

/** What the pin's row says about it. */
export function placedSizeNote(placed: PlacedSize, windowWidth: number): string {
  const width = placedWidth(placed);
  const band = sizeBand(width);
  if (placed.frame && placed.frame.width < placed.width) {
    return `Placed in a preview at ${band} width (${width}px). Switch the preview to that width to see it.`;
  }
  return `Placed at ${band} width (${width}px). This window is ${Math.round(windowWidth)}px wide.`;
}

/** How a ticket page describes it: "768 x 1024 (tablet width)", the frame's when there was one. */
export function placedSizeLabel(placed: PlacedSize): string {
  const shown = placed.frame ?? placed;
  const where = placed.frame && placed.frame.width < placed.width ? ` in a preview, in a ${placed.width}px window` : '';
  return `${shown.width} x ${shown.height} (${sizeBand(shown.width)} width)${where}`;
}

function side(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 1 && value <= MAX_SIDE ? Math.round(value) : null;
}

function box(value: unknown): { width: number; height: number } | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const width = side(record.width);
  const height = side(record.height);
  return width !== null && height !== null ? { width, height } : null;
}

/** A placed size read from anywhere it travels, or none: never a guess. */
export function parsePlacedSize(value: unknown): PlacedSize | undefined {
  const window = box(value);
  if (!window) return undefined;
  const frame = box((value as Record<string, unknown>).frame);
  return frame ? { ...window, frame } : window;
}
